import { motion } from "framer-motion";
import { Avatar } from "@/components/Avatar";
import { FORMATION, slotCoords } from "@/lib/football";

const Lines = () => (
  <svg viewBox="0 0 90 130" preserveAspectRatio="none" className="absolute inset-0 w-full h-full" fill="none" stroke="rgba(255,255,255,0.38)" strokeWidth="0.45">
    <rect x="2" y="2" width="86" height="126" rx="1" />
    <line x1="2" y1="65" x2="88" y2="65" />
    <circle cx="45" cy="65" r="9" />
    <circle cx="45" cy="65" r="0.8" fill="rgba(255,255,255,0.5)" />
    <rect x="20" y="2" width="50" height="17" />
    <rect x="33" y="2" width="24" height="6" />
    <rect x="39" y="0.4" width="12" height="1.6" stroke="rgba(255,255,255,0.7)" />
    <path d="M36 19 A9 9 0 0 0 54 19" />
    <rect x="20" y="111" width="50" height="17" />
    <rect x="33" y="122" width="24" height="6" />
    <rect x="39" y="128" width="12" height="1.6" stroke="rgba(255,255,255,0.7)" />
    <path d="M36 111 A9 9 0 0 1 54 111" />
  </svg>
);

const Node = ({ p, pos, stat, onTap, highlight }) => (
  <div className="absolute -translate-x-1/2 -translate-y-1/2 w-[19%] transition-[left,top] duration-500" style={{ left: `${pos.pitch_x}%`, top: `${pos.pitch_y}%` }}>
  <motion.button
    initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} whileTap={{ scale: 1.12 }} transition={{ type: "spring", stiffness: 300, damping: 22 }}
    data-testid={`pitch-player-${p.id}`} onClick={() => onTap?.(p)}
    className="w-full flex flex-col items-center focus:outline-none">
    <div className="relative">
      <Avatar user={p} className={`w-9 h-9 sm:w-10 sm:h-10 text-[11px] ${highlight ? "ring-2 ring-cyan-300" : ""}`}
        ring={p.team === "white" ? "border-2 border-white shadow-[0_2px_8px_rgba(0,0,0,0.5)]" : "border-2 border-zinc-950 shadow-[0_0_0_1.5px_rgba(255,255,255,0.35)]"} />
      {p.is_captain && <span className="absolute -top-1 -left-1 w-4 h-4 rounded-full bg-amber-400 text-zinc-950 text-[9px] font-black flex items-center justify-center">C</span>}
      {stat?.goals > 0 && <span data-testid={`pitch-goals-${p.id}`} className="absolute -top-1 -right-1.5 min-w-4 h-4 px-0.5 rounded-full bg-cyan-400 text-zinc-950 text-[9px] font-black flex items-center justify-center">{stat.goals}</span>}
      {stat?.assists > 0 && <span className="absolute -bottom-0.5 -right-1.5 min-w-4 h-4 px-0.5 rounded-full bg-zinc-100 text-zinc-900 text-[8px] font-black flex items-center justify-center">A{stat.assists}</span>}
    </div>
    <span className={`mt-0.5 max-w-full truncate rounded px-1 text-[9.5px] sm:text-[10.5px] font-bold leading-tight ${p.team === "white" ? "bg-white text-zinc-950" : "bg-zinc-950 text-white"}`}>{p.short_name}</span>
    <span className="num text-[8.5px] font-bold text-cyan-200 leading-tight drop-shadow">{pos.assigned_position}</span>
  </motion.button>
  </div>
);

const EmptySlot = ({ i, team }) => {
  const [x, y] = slotCoords(i, team);
  return (
    <div className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center" style={{ left: `${x}%`, top: `${y}%` }}>
      <div className={`w-8 h-8 rounded-full border-2 border-dashed ${team === "white" ? "border-white/50" : "border-zinc-950/70"}`} />
      <span className="num text-[8.5px] font-bold text-white/60">{FORMATION[i][0]}</span>
    </div>
  );
};

const TeamTag = ({ team, captain, className }) => (
  <div className={`absolute left-2.5 z-10 flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-black tracking-wider ${team === "white" ? "bg-white text-zinc-950" : "bg-zinc-950 text-white border border-white/20"} ${className}`}>
    <span className={`w-2 h-2 rounded-full ${team === "white" ? "bg-zinc-950" : "bg-white"}`} />
    {team === "white" ? "BLANC" : "NOIR"}
    {captain && <span className="font-semibold opacity-60 normal-case tracking-normal">· C. {captain}</span>}
  </div>
);

export const Pitch = ({ data, onTap, selectedId, maxH = 70 }) => {
  const byId = Object.fromEntries(data.players.map((p) => [p.id, p]));
  const stats = Object.fromEntries(data.stats.map((s) => [s.player_id, s]));
  const placed = data.positions.filter((p) => p.slot !== null && byId[p.player_id]);
  const used = { white: new Set(), black: new Set() };
  placed.forEach((p) => used[p.team].add(p.slot));
  const cap = (t) => byId[data.match[`captain_${t}`]]?.short_name;
  return (
    <div data-testid="pitch" className="relative mx-auto rounded-[28px] overflow-hidden border-4 border-white shadow-xl shadow-emerald-900/20"
      style={{ aspectRatio: "9 / 13", width: `min(100%, calc(${maxH}dvh * 9 / 13))`,
        background: "repeating-linear-gradient(180deg, #22a447 0 7.69%, #1c9440 7.69% 15.38%)" }}>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_45%,rgba(0,0,0,0.22))]" />
      <Lines />
      <TeamTag team="white" captain={cap("white")} className="top-2.5" />
      <TeamTag team="black" captain={cap("black")} className="bottom-2.5" />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-0 w-9 h-9 rounded-full bg-zinc-950/70 border border-cyan-300/50 flex items-center justify-center font-display font-black text-[11px] text-cyan-200">VS</div>
      {["white", "black"].map((t) => FORMATION.map((_, i) => (!used[t].has(i) ? <EmptySlot key={`${t}${i}`} i={i} team={t} /> : null)))}
      {placed.map((pos) => (
        <Node key={pos.player_id} p={byId[pos.player_id]} pos={pos} stat={stats[pos.player_id]} onTap={onTap} highlight={selectedId === pos.player_id} />
      ))}
    </div>
  );
};

export const Bench = ({ data, onTap }) => {
  const byId = Object.fromEntries(data.players.map((p) => [p.id, p]));
  const bench = data.positions.filter((p) => p.slot === null && byId[p.player_id]);
  if (!bench.length) return null;
  return (
    <div className="mt-4" data-testid="bench">
      <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Remplaçants</p>
      <div className="flex flex-wrap gap-2">
        {bench.map((b) => (
          <button key={b.player_id} onClick={() => onTap?.(byId[b.player_id])} data-testid={`bench-player-${b.player_id}`} className="flex items-center gap-2 rounded-full bg-white border border-slate-200 pl-1 pr-3 py-1">
            <Avatar user={byId[b.player_id]} className="w-7 h-7 text-[10px]" ring={b.team === "white" ? "border-2 border-slate-300" : "border-2 border-zinc-950"} />
            <span className="text-xs font-semibold">{byId[b.player_id].short_name}</span>
          </button>
        ))}
      </div>
    </div>
  );
};
